import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/ProfileThemes.css';

// DEMO: This showcases the customizable profile system with sample data
// In production, this data would come from the backend/database

function EnhancedProfile() {
  const location = useLocation();

  // Theme selection (in production, this comes from user's profile settings)
  const [currentTheme, setCurrentTheme] = useState('classic-spotlight');
  const [applauseCount, setApplauseCount] = useState(47);
  const [hasApplauded, setHasApplauded] = useState(false);
  const [activeCreditsTab, setActiveCreditsTab] = useState('theatre');
  const [layoutMode, setLayoutMode] = useState('scroll');
  const [heroAlignment, setHeroAlignment] = useState('center');
  const [activeSection, setActiveSection] = useState('about'); // For multi-page mode

  // Custom theme settings
  const [customTheme, setCustomTheme] = useState({
    primaryColor: '#D8A45D',
    secondaryColor: '#F9EFE6',
    accentColor: '#222222',
    backgroundColor: '#FAFAFA',
    textColor: '#222222',
    headingFont: 'Inter',
    bodyFont: 'Inter',
  });

  const [customMediaGallery, setCustomMediaGallery] = useState(null);

  // Load settings from navigation state if available (from preview button)
  useEffect(() => {
    if (location.state?.theme) {
      setCurrentTheme(location.state.theme);
    }
    if (location.state?.customTheme) {
      setCustomTheme(location.state.customTheme);
    }
    if (location.state?.layoutMode) {
      setLayoutMode(location.state.layoutMode);
    }
    if (location.state?.heroAlignment) {
      setHeroAlignment(location.state.heroAlignment);
    }
    if (location.state?.mediaGallery) {
      setCustomMediaGallery(location.state.mediaGallery);
    }
  }, [location.state]);

  // Sample profile data (in production, from database)
  const profileData = {
    name: 'Alexandra Rivers',
    tagline: 'Actor • Singer • Dancer',
    headshot: null, // Would be image URL
    headshotShape: 'circle', // or "square"
    bannerImage: null,
    bio: `Alexandra Rivers is a New York-based performer with over 15 years of experience across theatre, film, and television.

She holds a BFA in Musical Theatre from NYU Tisch and has performed in productions across Broadway, Off-Broadway, and regional theaters nationwide.

Known for her versatility and emotional depth, Alexandra brings authenticity and passion to every role—from classical drama to contemporary musical theatre.`,

    contactLinks: [
      { type: 'email', label: 'Email', url: 'mailto:alex@showcase.com' },
      { type: 'website', label: 'Website', url: '#' },
      { type: 'instagram', label: 'Instagram', url: '#' },
      { type: 'agent', label: 'Agent Contact', url: '#' },
    ],

    credits: {
      theatre: [
        { role: 'Elphaba', production: 'Wicked', company: 'Broadway', year: '2023' },
        { role: 'Fantine', production: 'Les Misérables', company: 'National Tour', year: '2022' },
        { role: 'Maureen', production: 'Rent', company: 'Off-Broadway Revival', year: '2021' },
        {
          role: 'Angelica Schuyler',
          production: 'Hamilton',
          company: 'Chicago Production',
          year: '2020',
        },
      ],
      film: [
        { role: 'Sarah', production: 'The Last Summer', company: 'Independent Film', year: '2023' },
        { role: 'Supporting', production: 'City Lights', company: 'Feature Film', year: '2022' },
      ],
      voice: [
        {
          role: 'Princess Elena',
          production: 'Quest for the Crown',
          company: 'Animated Series',
          year: '2023',
        },
        {
          role: 'Various',
          production: 'Commercial Voice Work',
          company: 'National Campaigns',
          year: '2020-Present',
        },
      ],
    },

    mediaGallery: [
      {
        type: 'video',
        title: 'Defying Gravity - Wicked',
        url: 'https://www.youtube.com/embed/FlMBcTGJ4YM',
      },
      {
        type: 'video',
        title: 'Contemporary Monologue Reel',
        url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      },
      { type: 'image', title: 'Headshot 2024', url: null },
      { type: 'image', title: 'Production Still - Hamilton', url: null },
      { type: 'video', title: 'Dance Reel', url: 'https://www.youtube.com/embed/dQw4w9WgXcQ' },
      { type: 'image', title: 'Behind the Scenes', url: null },
    ],

    customBlocks: [
      {
        title: 'Awards & Recognition',
        content: `• **Best Actress in a Musical** - Regional Theatre Awards 2023
• **Rising Star Award** - Broadway World 2022
• **Outstanding Performance** - Off-Broadway Alliance 2021
• **Dean's List** - NYU Tisch School of the Arts`,
      },
      {
        title: 'Special Skills',
        content: `**Vocal Range:** Mezzo-Soprano (G3-C6)
**Dance:** Ballet, Jazz, Contemporary, Tap
**Accents:** British RP, Southern American, New York
**Other:** Stage Combat, Aerial Silks, Piano`,
      },
      {
        title: 'Upcoming Projects',
        content: `**Spring 2025:** Leading role in new musical workshop at The Public Theater
**Summer 2025:** Guest artist at Williamstown Theatre Festival
**Fall 2025:** Developing original solo show`,
      },
    ],
  };

  const handleApplause = () => {
    if (!hasApplauded) {
      setApplauseCount((prev) => prev + 1);
      setHasApplauded(true);
    }
  };

  const renderCreditsTable = (credits) => {
    return (
      <table className="profile-credits-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Production</th>
            <th>Company/Theatre</th>
            <th>Year</th>
          </tr>
        </thead>
        <tbody>
          {credits.map((credit, index) => (
            <tr key={index}>
              <td>{credit.role}</td>
              <td>{credit.production}</td>
              <td>{credit.company}</td>
              <td>{credit.year}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Dynamic styles for custom theme
  const customThemeStyles =
    currentTheme === 'custom'
      ? {
          '--profile-primary': customTheme.primaryColor,
          '--profile-secondary': customTheme.secondaryColor,
          '--profile-accent': customTheme.accentColor,
          '--profile-bg': customTheme.backgroundColor,
          '--profile-text': customTheme.textColor,
          '--profile-font-heading': customTheme.headingFont,
          '--profile-font-body': customTheme.bodyFont,
        }
      : {};

  return (
    <div
      className="profile-page"
      data-profile-theme={currentTheme}
      data-layout-mode={layoutMode}
      data-hero-alignment={heroAlignment}
      style={customThemeStyles}
    >
      {/* Theme Switcher (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div
          style={{
            position: 'fixed',
            top: '80px',
            left: '20px',
            zIndex: 1001,
            background: 'rgba(0,0,0,0.8)',
            padding: '16px',
            borderRadius: '8px',
            color: 'white',
          }}
        >
          <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '600' }}>
            DEMO: Theme Switcher
          </div>
          <select
            value={currentTheme}
            onChange={(e) => setCurrentTheme(e.target.value)}
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: '2px solid #f3e178',
              background: '#1a1a1a',
              color: '#f3e178',
              width: '200px',
            }}
          >
            <option value="classic-spotlight">Classic Spotlight</option>
            <option value="modern-stage">Modern Stage</option>
            <option value="minimal">Minimal</option>
            <option value="custom">Custom Theme</option>
          </select>

          {/* Layout Controls */}
          <div
            style={{
              marginTop: '16px',
              fontSize: '11px',
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '12px',
            }}
          >
            <div style={{ marginBottom: '8px' }}>Hero Alignment:</div>
            <select
              value={heroAlignment}
              onChange={(e) => setHeroAlignment(e.target.value)}
              style={{
                width: '100%',
                padding: '6px',
                borderRadius: '4px',
                border: '1px solid #f3e178',
                background: '#1a1a1a',
                color: '#f3e178',
                marginBottom: '12px',
                fontSize: '11px',
              }}
            >
              <option value="center">Center-Aligned</option>
              <option value="side">Side-Aligned</option>
            </select>

            <div style={{ marginBottom: '8px' }}>Page Layout:</div>
            <select
              value={layoutMode}
              onChange={(e) => setLayoutMode(e.target.value)}
              style={{
                width: '100%',
                padding: '6px',
                borderRadius: '4px',
                border: '1px solid #f3e178',
                background: '#1a1a1a',
                color: '#f3e178',
                fontSize: '11px',
              }}
            >
              <option value="scroll">Single Scroll Page</option>
              <option value="multipage">Multi-Page Navigation</option>
            </select>
          </div>

          {/* Custom Theme Color Pickers (for demo) */}
          {currentTheme === 'custom' && (
            <div
              style={{
                marginTop: '16px',
                fontSize: '11px',
                borderTop: '1px solid rgba(255,255,255,0.2)',
                paddingTop: '12px',
              }}
            >
              <div style={{ marginBottom: '8px' }}>Customize Colors:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ width: '80px' }}>Primary:</label>
                  <input
                    type="color"
                    value={customTheme.primaryColor}
                    onChange={(e) =>
                      setCustomTheme({ ...customTheme, primaryColor: e.target.value })
                    }
                    style={{ width: '40px', height: '24px', cursor: 'pointer' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ width: '80px' }}>Secondary:</label>
                  <input
                    type="color"
                    value={customTheme.secondaryColor}
                    onChange={(e) =>
                      setCustomTheme({ ...customTheme, secondaryColor: e.target.value })
                    }
                    style={{ width: '40px', height: '24px', cursor: 'pointer' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ width: '80px' }}>Accent:</label>
                  <input
                    type="color"
                    value={customTheme.accentColor}
                    onChange={(e) =>
                      setCustomTheme({ ...customTheme, accentColor: e.target.value })
                    }
                    style={{ width: '40px', height: '24px', cursor: 'pointer' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ width: '80px' }}>Background:</label>
                  <input
                    type="color"
                    value={customTheme.backgroundColor}
                    onChange={(e) =>
                      setCustomTheme({ ...customTheme, backgroundColor: e.target.value })
                    }
                    style={{ width: '40px', height: '24px', cursor: 'pointer' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ width: '80px' }}>Text:</label>
                  <input
                    type="color"
                    value={customTheme.textColor}
                    onChange={(e) => setCustomTheme({ ...customTheme, textColor: e.target.value })}
                    style={{ width: '40px', height: '24px', cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* HERO / HEADER SECTION */}
      <section className="profile-hero profile-section">
        {profileData.bannerImage && (
          <div
            className="profile-hero-bg"
            style={{ backgroundImage: `url(${profileData.bannerImage})` }}
          ></div>
        )}
        <div className="profile-hero-spotlight"></div>

        <div className="profile-hero-content">
          {profileData.headshot ? (
            <img
              src={profileData.headshot}
              alt={profileData.name}
              className={`profile-headshot ${profileData.headshotShape}`}
            />
          ) : (
            <div
              className={`profile-headshot ${profileData.headshotShape}`}
              style={{
                background: 'linear-gradient(135deg, #600015 0%, #b87a84 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '64px',
                fontWeight: '800',
              }}
            >
              {profileData.name.charAt(0)}
            </div>
          )}

          <div className="profile-hero-text">
            <h1 className="profile-name">{profileData.name}</h1>
            <div className="profile-tagline">{profileData.tagline}</div>

            <div className="profile-contact-buttons">
              {profileData.contactLinks.map((link, index) => (
                <a key={index} href={link.url} className="profile-contact-btn">
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section Navigation for Multi-Page Mode */}
      {layoutMode === 'multipage' && (
        <nav className="profile-section-nav">
          <div className="profile-section-nav-container">
            <button
              className={`profile-nav-item ${activeSection === 'about' ? 'active' : ''}`}
              onClick={() => setActiveSection('about')}
            >
              About
            </button>
            <button
              className={`profile-nav-item ${activeSection === 'credits' ? 'active' : ''}`}
              onClick={() => setActiveSection('credits')}
            >
              Credits
            </button>
            <button
              className={`profile-nav-item ${activeSection === 'media' ? 'active' : ''}`}
              onClick={() => setActiveSection('media')}
            >
              Media
            </button>
            <button
              className={`profile-nav-item ${activeSection === 'awards' ? 'active' : ''}`}
              onClick={() => setActiveSection('awards')}
            >
              Awards & Skills
            </button>
          </div>
        </nav>
      )}

      <div className="profile-container">
        {/* BIO / ABOUT SECTION */}
        {(layoutMode === 'scroll' || activeSection === 'about') && (
          <section className="profile-section section-content">
            <h2 className="profile-section-title">About</h2>
            <div className="profile-bio">
              {profileData.bio.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </section>
        )}

        {/* RESUME / CREDITS SECTION */}
        {(layoutMode === 'scroll' || activeSection === 'credits') && (
          <section className="profile-section section-content">
            <h2 className="profile-section-title">Credits</h2>

            <div className="profile-credits-tabs">
              <button
                className={`profile-credits-tab ${activeCreditsTab === 'theatre' ? 'active' : ''}`}
                onClick={() => setActiveCreditsTab('theatre')}
              >
                Theatre
              </button>
              <button
                className={`profile-credits-tab ${activeCreditsTab === 'film' ? 'active' : ''}`}
                onClick={() => setActiveCreditsTab('film')}
              >
                Film & TV
              </button>
              <button
                className={`profile-credits-tab ${activeCreditsTab === 'voice' ? 'active' : ''}`}
                onClick={() => setActiveCreditsTab('voice')}
              >
                Voice
              </button>
            </div>

            {renderCreditsTable(profileData.credits[activeCreditsTab])}
          </section>
        )}

        {/* MEDIA GALLERY SECTION */}
        {(layoutMode === 'scroll' || activeSection === 'media') && (
          <section className="profile-section section-content">
            <h2 className="profile-section-title">Media</h2>

            <div className="profile-gallery">
              {(customMediaGallery && customMediaGallery.length > 0
                ? customMediaGallery
                : profileData.mediaGallery
              ).map((item, index) => (
                <div key={index} className="profile-gallery-item">
                  {item.type === 'video' ? (
                    <iframe
                      src={item.embedUrl || item.url}
                      title={item.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, #600015 20%, #b87a84 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '48px',
                      }}
                    >
                      📷
                    </div>
                  )}
                  <div className="profile-gallery-item-overlay">{item.title}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CUSTOM BLOCKS SECTION */}
        {(layoutMode === 'scroll' || activeSection === 'awards') && (
          <>
            {profileData.customBlocks.map((block, index) => (
              <section key={index} className="profile-section section-content">
                <div className="profile-custom-block">
                  <h3>{block.title}</h3>
                  <div className="profile-custom-block-content">
                    {block.content.split('\n').map((line, i) => {
                      // Simple markdown parsing for bold and bullets
                      if (line.startsWith('•')) {
                        const parts = line.substring(1).split('**');
                        return (
                          <p key={i}>
                            •
                            {parts.map((part, j) =>
                              j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                            )}
                          </p>
                        );
                      } else if (line.startsWith('**')) {
                        const parts = line.split('**');
                        return (
                          <p key={i}>
                            {parts.map((part, j) =>
                              j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                            )}
                          </p>
                        );
                      }
                      return <p key={i}>{line}</p>;
                    })}
                  </div>
                </div>
              </section>
            ))}
          </>
        )}
      </div>

      {/* APPLAUSE BUTTON */}
      <div className="profile-applause">
        <button
          className={`profile-applause-btn ${hasApplauded ? 'applauded' : ''}`}
          onClick={handleApplause}
          title="Applaud this performance!"
        >
          👏
          {applauseCount > 0 && <span className="profile-applause-count">{applauseCount}</span>}
        </button>
      </div>
    </div>
  );
}

export default EnhancedProfile;
